
import { useEffect, useRef, useState } from 'react';
import { productCategories } from '../../../mocks/products';
import ProductImage from '../../../components/base/ProductImage';

export default function ProductsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative bg-white py-12 pb-20 px-8 md:px-12 lg:px-[8%]"
    >
      <div className="max-w-[1400px] mx-auto">
        {productCategories.map((category, catIndex) => (
          <div
            key={category.label}
            className={`mb-16 transition-all duration-700 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
            style={{ transitionDelay: `${catIndex * 150}ms` }}
          >
            {/* Cabecera de categoría */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-[32px]">{category.emoji}</span>
                <h2 className="text-[clamp(1.6rem,3vw,2.2rem)] font-bold text-[#222222]">
                  {category.label}
                </h2>
              </div>
              <div
                className="h-[3px] w-[80px] rounded-full"
                style={{ backgroundColor: category.color }}
              ></div>
            </div>

            {/* Grid de productos */}
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              data-product-shop
            >
              {category.products.map((product, prodIndex) => (
                <a
                  key={product.name}
                  href={product.link}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className={`group block bg-white rounded-lg overflow-hidden border border-[#E8E5E0] hover:border-[#D4C5B8] hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer ${
                    inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${300 + prodIndex * 100}ms` }}
                >
                  {/* Imagen del producto */}
                  <div className="w-full h-[280px] bg-[#FAFAF9] overflow-hidden flex items-center justify-center">
                    <ProductImage
                      src={product.image}
                      alt={product.name}
                      title={`${product.name} - LunaFinds`}
                      className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>

                  {/* Contenido */}
                  <div className="p-5">
                    <h3 className="text-[16px] font-semibold text-[#222222] mb-2 leading-snug line-clamp-2 min-h-[48px]">
                      {product.name}
                    </h3>

                    <p
                      className="text-[18px] font-bold mb-3"
                      style={{ color: category.color }}
                    >
                      {product.price}
                    </p>

                    <p className="text-[14px] text-[#6B6860] leading-relaxed mb-4 line-clamp-3 min-h-[63px]">
                      &ldquo;{product.description}&rdquo;
                    </p>

                    {/* Botón */}
                    <div
                      className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-[14px] font-medium transition-all duration-300 whitespace-nowrap"
                      style={{
                        backgroundColor: category.bgColor,
                        color: category.color,
                      }}
                    >
                      <span>Ver en Amazon</span>
                      <i className="ri-arrow-right-up-line text-[16px] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"></i>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
